import { ApiRequestError } from './api'

/**
 * Whether a typed access token is one the API will actually answer.
 *
 * The gate used to store whatever was typed and navigate straight into the
 * console, which meant a typo "signed you in": `/admin` rendered its frame,
 * every request behind it 401'd, and what the person saw was a red banner over
 * whatever React Query still had cached from the last credential — real
 * appointments, real names, none of them refreshed. The one question the gate
 * exists to answer, it did not ask.
 *
 * Refused and unreachable are kept apart on purpose. A boutique whose wifi has
 * dropped must not be told its token is wrong: that sends a stylist hunting for
 * a new credential during an appointment, which is the most expensive possible
 * wrong answer. Only a 401 or 403 — the server, having looked, saying no — is
 * reported as a bad token.
 */
export interface TokenVerdict {
  readonly ok: boolean
  /** Absent when `ok`. */
  readonly message?: string
}

const ACCEPTED: TokenVerdict = { ok: true }

export async function verifySharedToken(
  ask: (token: string) => Promise<unknown>,
  raw: string,
): Promise<TokenVerdict> {
  const token = raw.trim()
  if (!token) return { ok: false, message: 'Enter the access token.' }

  try {
    await ask(token)
    return ACCEPTED
  } catch (error) {
    if (error instanceof ApiRequestError && (error.status === 401 || error.status === 403)) {
      return { ok: false, message: 'That token was not accepted. Check it and try again.' }
    }
    /*
     * Anything else — offline, DNS, a 500, the API not deployed. The token may
     * be perfectly good, so the sentence says where the fault is rather than
     * blaming what was typed.
     */
    return { ok: false, message: 'Could not reach the boutique’s server. Check this device’s connection.' }
  }
}
